/**
 * Получает начало дня для указанной даты (00:00:00.000)
 * @param date - дата для обработки
 * @returns новый объект Date с временем 00:00:00.000
 */
const getStartOfDay = (date: Date): Date => {
    const result = new Date(date);
    result.setHours(0, 0, 0, 0);

    return result;
};

/**
 * Вычисляет разницу в днях между двумя датами
 * @param laterDate - более поздняя дата
 * @param earlierDate - более ранняя дата
 * @returns количество полных дней между датами
 */
const getDaysDifference = (laterDate: Date, earlierDate: Date): number => {
    const msPerDay = 1000 * 60 * 60 * 24;
    const laterMs = laterDate.getTime();
    const earlierMs = earlierDate.getTime();

    return Math.floor((laterMs - earlierMs) / msPerDay);
};

const RUSSIAN_MONTHS_GENITIVE = [
    'января',
    'февраля',
    'марта',
    'апреля',
    'мая',
    'июня',
    'июля',
    'августа',
    'сентября',
    'октября',
    'ноября',
    'декабря',
] as const;

const DAYS_AGO_BY_DAYS_DIFF: Record<number, string> = {
    [0]: 'Сегодня',
    [1]: 'Вчера',
    [2]: '2 дня назад',
    [3]: '3 дня назад',
} as const;

/**
 * Форматирует дату в российский формат "d MMMM yyyy"
 * @param date - дата для форматирования
 * @returns строка в формате "8 октября 2025"
 */
const formatDateToRussian = (date: Date): string => {
    const day = date.getDate();
    const month = RUSSIAN_MONTHS_GENITIVE[date.getMonth()];
    const year = date.getFullYear();

    return `${day} ${month} ${year}`;
};

/**
 * Форматирует дату в краткий российский формат "d MMMM" (без года)
 * @param dateString - строка с датой в формате ISO 8601 (например, "2025-11-23T00:00:00.000Z")
 * @returns строка в формате "23 ноября"
 *
 * @example
 * formatDateToShortRussian('2025-11-23T00:00:00.000Z') // "23 ноября"
 * formatDateToShortRussian('2025-01-15T12:30:00.000Z') // "15 января"
 */
export const formatDateToShortRussian = (dateString: string): string => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = RUSSIAN_MONTHS_GENITIVE[date.getMonth()];

    return `${day} ${month}`;
};
/**
 * Вычисляет количество дней до указанной даты
 * @param dateString - строка с датой в формате ISO 8601 (например, "2025-12-31T00:00:00.000Z")
 * @returns количество дней до указанной даты
 * (положительное число для будущих дат, 0 для сегодня, отрицательное для прошедших)
 *
 * @example
 * getDaysUntil('2025-12-31T00:00:00.000Z') // 46 (если сегодня 15 ноября 2025)
 * getDaysUntil('2025-11-15T00:00:00.000Z') // 0 (если сегодня 15 ноября 2025)
 * getDaysUntil('2025-11-10T00:00:00.000Z') // -5 (если сегодня 15 ноября 2025)
 */
export const getDaysUntil = (dateString?: string): number => {
    if (!dateString) {
        return -1;
    }

    // Парсим целевую дату и явно конвертируем в московское время для консистентности
    const targetDate = new Date(new Date(dateString).toLocaleString('en-US', { timeZone: 'Europe/Moscow' }));
    // Получаем текущее время в московском часовом поясе
    const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Moscow' }));

    // Получаем начало дня для корректного сравнения (теперь обе даты в московском времени)
    const targetStart = getStartOfDay(targetDate);
    const nowStart = getStartOfDay(now);

    // Вычисляем разницу в днях (положительное значение если дата в будущем)
    return getDaysDifference(targetStart, nowStart);
};

/**
 * Форматирует дату в российский формат с поддержкой относительных дат
 * @param dateString - строка с датой в формате ISO 8601 (например, "2025-10-08T12:00:00Z")
 * @returns отформатированная дата (например, "Сегодня", "Вчера", "3 дня назад", "8 октября 2025")
 *
 * @example
 * formatRussianDate('2025-10-11T12:00:00Z') // "Сегодня"
 * formatRussianDate('2025-10-10T12:00:00Z') // "Вчера"
 * formatRussianDate('2025-10-09T12:00:00Z') // "2 дня назад"
 * formatRussianDate('2025-10-08T12:00:00Z') // "3 дня назад"
 * formatRussianDate('2025-10-07T12:00:00Z') // "7 октября 2025"
 */
export const formatRussianDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();

    // Получаем начало дня для корректного сравнения
    const dateStart = getStartOfDay(date);
    const nowStart = getStartOfDay(now);

    // Вычисляем разницу в днях
    const daysDiff = getDaysDifference(nowStart, dateStart);

    // Для всех остальных случаев возвращаем обычный формат даты
    return DAYS_AGO_BY_DAYS_DIFF[daysDiff] || formatDateToRussian(date);
};

/**
 * Возвращает текущий год
 * @returns текущий год в формате YYYY
 *
 * @example
 * getCurrentYear() // 2025
 */
export const getCurrentYear = (): number => {
    return new Date().getFullYear();
};
